// ============================================================================
// TagPickerDialog.tsx — выбор тегов (дерево + поиск + создание).
// Кнопки «Сохранить»/«Отмена»/«Новый тег» — СВЕРХУ (шапка UniversalWindow).
// «Новый тег» — модалка ввода (promptDialog — единый механизм платформы).
// Дубликат имени на том же уровне (сравнение как у сервера — после trim,
// без учёта регистра): тег считается выбранным, окно закрывается с сохранением.
// ============================================================================

import { useEffect, useMemo, useState } from 'react';
import { Check, Search } from 'lucide-react';
import { UniversalWindow } from '@/components/ui/UniversalWindow';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { promptDialog } from '@/lib/dialogs';
import { friendlyError, logErrorToJournal } from '@/lib/errors';
import { useToastStore } from '@/store/toastStore';
import { listTags, saveTag, type TagInfo } from '@/lib/tags';

interface Props {
  open: boolean;
  onClose: () => void;
  selectedIds: string[];
  onSave: (tagIds: string[]) => Promise<void> | void;
  title?: string;
}

// Согласовано с сервером (metadata.repository_tags_admin.ensureTagNameUnique):
// сравниваются имена ПОСЛЕ trim, без учёта регистра, в пределах одного
// родителя; новые теги создаются в корне (parentId = '').
function findExistingTag(all: TagInfo[], name: string): TagInfo | undefined {
  const needle = name.trim().toLowerCase();
  return all.find((t) => (t.parentId || '') === '' && t.name.trim().toLowerCase() === needle);
}

export function TagPickerDialog({ open, onClose, selectedIds, onSave, title }: Props) {
  const [tags, setTags] = useState<TagInfo[]>([]);
  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setSelected(new Set(selectedIds));
    setQuery('');
    listTags('')
      .then(setTags)
      .catch((e) => {
        logErrorToJournal(e, 'tags.list');
        useToastStore.getState().showError(friendlyError(e));
      });
    // selectedIds — только стартовое значение при открытии
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const ordered = useMemo(() => {
    const byParent = new Map<string, TagInfo[]>();
    for (const t of tags) {
      const p = t.parentId || '';
      if (!byParent.has(p)) byParent.set(p, []);
      byParent.get(p)!.push(t);
    }
    for (const arr of byParent.values()) arr.sort((a, b) => a.name.localeCompare(b.name, 'ru'));
    const out: Array<{ tag: TagInfo; depth: number }> = [];
    const walk = (parent: string, depth: number) => {
      for (const t of byParent.get(parent) || []) {
        out.push({ tag: t, depth });
        walk(t.id, depth + 1);
      }
    };
    walk('', 0);
    return out;
  }, [tags]);

  const filtered = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) return null;
    return tags.filter((t) => t.name.toLowerCase().includes(needle));
  }, [tags, query]);

  const toggle = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  // Ошибки сервера — тост (friendlyError) + журнал; окно НЕ закрывается.
  const reportError = (e: unknown, scope: string) => {
    logErrorToJournal(e, scope);
    useToastStore.getState().showError(friendlyError(e));
  };

  // Сохранение НАБОРА и закрытие окна (успех) — единая точка.
  const persist = async (ids: Set<string>) => {
    if (busy) return;
    setBusy(true);
    try {
      await onSave(Array.from(ids));
      onClose();
    } catch (e) {
      reportError(e, 'tags.setSubjects');
    } finally {
      setBusy(false);
    }
  };

  // «Новый тег»: модалка ввода (promptDialog). Дубликат имени того же уровня —
  // закрыть окно с сохранением (тег уже выбран); новый тег — сохранить на
  // сервере, добавить в выбранные и ПРОДОЛЖИТЬ выбор в этом окне.
  const createTag = async () => {
    if (busy) return;
    const input = await promptDialog('Наименование нового тега', '', 'Новый тег', 'Например: Оплата');
    if (input === null) return;
    const name = input.trim();
    if (!name) return;
    const existing = findExistingTag(tags, name);
    if (existing) {
      const ids = new Set(selected);
      ids.add(existing.id);
      setSelected(ids);
      await persist(ids);
      return;
    }
    setBusy(true);
    try {
      const id = await saveTag({ name });
      const fresh = await listTags('');
      setTags(fresh);
      if (id) setSelected((prev) => new Set(prev).add(id));
    } catch (e) {
      reportError(e, 'tags.save');
    } finally {
      setBusy(false);
    }
  };

  if (!open) return null;

  const renderRow = (t: TagInfo, depth: number) => {
    const isSel = selected.has(t.id);
    return (
      <button
        key={t.id}
        type="button"
        onClick={() => toggle(t.id)}
        className={`w-full flex items-center gap-2 rounded px-2 py-1 text-left text-xs hover:bg-accent/40 ${isSel ? 'bg-primary/10' : ''}`}
        style={{ paddingLeft: 8 + depth * 16 }}
      >
        <span className={`w-3.5 h-3.5 rounded border flex items-center justify-center shrink-0 ${isSel ? 'bg-primary border-primary text-primary-foreground' : 'border-border'}`}>
          {isSel && <Check className="h-2.5 w-2.5" />}
        </span>
        <span className="truncate">{t.name}</span>
        {t.usageCount > 0 && <span className="ml-auto text-[10px] text-muted-foreground">{t.usageCount}</span>}
      </button>
    );
  };

  const topBar = (
    <>
      <Button size="sm" disabled={busy} onClick={() => void persist(selected)}>Сохранить</Button>
      <Button size="sm" variant="outline" onClick={onClose}>Отмена</Button>
      <Button size="sm" variant="outline" disabled={busy} onClick={() => void createTag()}>Новый тег</Button>
    </>
  );

  return (
    <UniversalWindow
      open
      title={title || 'Теги'}
      width="w-[min(420px,94vw)]"
      height="h-[min(70vh,86vh)]"
      resizable={false}
      topBar={topBar}
      onClose={onClose}
    >
      <div className="flex flex-col gap-2 p-1 h-full">
        <div className="relative">
          <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
          <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Поиск тега…" className="h-8 pl-7 text-xs" autoFocus />
        </div>
        <div className="flex-1 overflow-y-auto rounded border border-border/50 p-1">
          {filtered ? (
            filtered.length === 0
              ? <div className="text-xs text-muted-foreground text-center py-6">Ничего не найдено</div>
              : filtered.map((t) => renderRow(t, 0))
          ) : (
            ordered.length === 0
              ? <div className="text-xs text-muted-foreground text-center py-6">Тегов пока нет — создайте первый</div>
              : ordered.map(({ tag, depth }) => renderRow(tag, depth))
          )}
        </div>
      </div>
    </UniversalWindow>
  );
}
